import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listStoneRequirements, listStoneQuotes, listStoneSuppliers } from '@/utils/api';
import { toast } from 'sonner';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import StonesHeader from '@/components/stones/StonesHeader';
import { Button } from '@/components/ui/button';
import { Gem, Users, Plus, FileText, TrendingUp, LayoutDashboard } from 'lucide-react';
import { STONE_REQUIREMENT_STATUSES, STONE_QUOTE_STATUSES } from '@/utils/constants';

const STATUS_LABEL = Object.fromEntries(STONE_REQUIREMENT_STATUSES.map(s => [s.value, s]));

export default function StoneDashboardPage() {
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      listStoneRequirements().then(r => r.data.data || []),
      listStoneQuotes().then(r => r.data.data || []),
      listStoneSuppliers().then(r => r.data.data || []),
    ]).then(([r, q, s]) => { setReqs(r); setQuotes(q); setSuppliers(s); })
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const openCount = reqs.filter(r => r.status === 'open').length;
  const closedCount = reqs.filter(r => r.status === 'closed').length;
  const submittedQuotes = quotes.filter(q => q.status === 'submitted').length;
  const acceptedQuotes = quotes.filter(q => q.status === 'accepted').length;
  const activeSuppliers = suppliers.filter(s => s.status === 'approved').length;

  const recent = reqs.slice(0, 5);
  const recentQuotes = quotes.slice(0, 5);

  return (
    <CollapsibleLayout>
      <StoneTabs active="dashboard" />
      <StonesHeader icon={LayoutDashboard} title="Stones Dashboard" subtitle="Overview of requirements and supplier activity">
        <Button onClick={() => navigate('/stones/new')} className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="new-req-btn">
          <Plus size={13} className="mr-1" /> New Requirement
        </Button>
      </StonesHeader>
      <div className="max-w-[1400px] mx-auto p-4 sm:p-6 space-y-5">

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Card onClick={() => navigate('/stones?status=open')} icon={Gem} label="Open Requirements" value={openCount} color="emerald" />
              <Card onClick={() => navigate('/stones?status=closed')} icon={FileText} label="Closed" value={closedCount} color="blue" />
              <Card icon={TrendingUp} label="Pending Quotes" value={submittedQuotes} color="amber" />
              <Card icon={TrendingUp} label="Accepted Quotes" value={acceptedQuotes} color="emerald" />
              <Card onClick={() => navigate('/stones/suppliers')} icon={Users} label="Active Suppliers" value={activeSuppliers} color="slate" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Recent requirements */}
              <section className="bg-white rounded-lg border">
                <div className="px-4 py-2.5 border-b font-medium text-sm">Recent Requirements</div>
                {recent.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">No requirements yet.</p>
                ) : (
                  <ul className="divide-y">
                    {recent.map(r => {
                      const status = STATUS_LABEL[r.status] || { label: r.status, color: '' };
                      return (
                        <li key={r.id} onClick={() => navigate(`/stones/${r.id}`)}
                          className="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between gap-2"
                          data-testid={`recent-req-${r.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold">{r.requirement_no}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${status.color}`}>{status.label}</span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {r.gemstone_type} {r.size_mm ? `· ${r.size_mm}` : ''} {r.qty ? `· Qty ${r.qty}` : ''}
                            </div>
                          </div>
                          <div className="text-[10px] text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* Recent quotes */}
              <section className="bg-white rounded-lg border">
                <div className="px-4 py-2.5 border-b font-medium text-sm">Recent Quotes</div>
                {recentQuotes.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">No quotes yet.</p>
                ) : (
                  <ul className="divide-y">
                    {recentQuotes.map(q => {
                      const qstatus = STONE_QUOTE_STATUSES[q.status] || { label: q.status, color: '' };
                      return (
                        <li key={q.id} onClick={() => navigate(`/stones/${q.requirement_id}`)}
                          className="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between gap-2"
                          data-testid={`recent-quote-${q.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium truncate">{q.supplier_name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${qstatus.color}`}>{qstatus.label}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {q.requirement_no} {q.price_total != null && `· ${q.currency} ${Number(q.price_total).toLocaleString()}`}
                            </div>
                          </div>
                          <div className="text-[10px] text-muted-foreground whitespace-nowrap">{new Date(q.created_at).toLocaleDateString()}</div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </CollapsibleLayout>
  );
}

function Card({ icon: Icon, label, value, color, onClick }) {
  // Match the rest of the app's pop-of-colour pattern: a tinted card with a
  // saturated icon chip and bold value. Hover lifts the border.
  const palette = {
    emerald: { bg: 'from-emerald-50 to-emerald-100/40 border-emerald-200', chip: 'bg-emerald-600 text-white', value: 'text-emerald-900' },
    blue:    { bg: 'from-blue-50 to-blue-100/40 border-blue-200',          chip: 'bg-blue-600 text-white',    value: 'text-blue-900' },
    amber:   { bg: 'from-amber-50 to-amber-100/40 border-amber-200',       chip: 'bg-amber-500 text-white',   value: 'text-amber-900' },
    slate:   { bg: 'from-slate-50 to-slate-100/40 border-slate-200',       chip: 'bg-slate-600 text-white',   value: 'text-slate-900' },
  };
  const p = palette[color] || palette.slate;
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`bg-gradient-to-br ${p.bg} rounded-xl border p-4 text-left ${onClick ? 'hover:border-teal-400 hover:shadow-md transition-all cursor-pointer' : 'cursor-default'}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${p.chip}`}>
        <Icon size={18} />
      </div>
      <div className={`text-2xl font-bold leading-none ${p.value}`}>{value}</div>
      <div className="text-[10px] text-slate-600 mt-1.5 leading-tight font-medium uppercase tracking-wide">{label}</div>
    </button>
  );
}
